// ContactUs.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // <-- Important
import { motion } from 'framer-motion';
import { FaFacebookF, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import { FaTiktok } from 'react-icons/fa6';
import contactUsBg from '../assets/Contact/pexels-chanthusa-wijayawickrama-362149114-14372006.jpg';

// Motion variants
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const ContactUs = () => {
    const { t } = useTranslation('contactus'); // Use contactus namespace

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        const phoneNumber = '94770483364';
        const text = `Aamy Tours Sri Lanka Inquiry
Name: ${formData.name}
Email: ${formData.email}
Contact Number: ${formData.contactNumber}
Message: ${formData.message}`;
        const formattedMessage = encodeURIComponent(text);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${formattedMessage}`;

        try {
            window.open(whatsappUrl, '_blank');
            setTimeout(() => {
                setIsSubmitting(false);
                setSuccessMessage(t('successMsg'));
                setFormData({ name: '', email: '', contactNumber: '', message: '' });
            }, 1000);
        } catch (error) {
            setIsSubmitting(false);
            setErrorMessage(t('errorMsg'));
        }
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen">
            {/* HERO SECTION */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh] overflow-hidden"
            >
                <img
                    src={contactUsBg}
                    alt="Contact Aamy Tours Sri Lanka"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />

                <div className="container relative z-10 flex flex-col items-center justify-center pt-[80px] px-4">
                    <h1 className="text-center text-[50px] font-bold leading-[70px] text-white font-konkhmer md:text-[60px] lg:text-[80px]">
                        <span
                            style={{
                                background: 'linear-gradient(to right, #166880, #D5A44F)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                textShadow: '0 0 12px rgba(22, 104, 128, 0.4)',
                            }}
                        >
                            {t('heroTitle')}
                        </span>
                    </h1>
                    <p className="mt-6 max-w-[90%] text-center font-inknut text-lg font-medium text-white/90 sm:mt-10 sm:max-w-3xl sm:text-2xl">
                        {t('heroSubtitle')}
                    </p>

                    {/* Decorative SVGs remain unchanged */}
                    <div className="absolute left-6 top-12 md:left-10 md:top-16">
                        <svg width="320" height="280" viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M320 1H1V279.5" stroke="url(#gradient1)" strokeWidth="1.5" />
                            <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#166880" />
                                    <stop offset="100%" stopColor="#D5A44F" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div className="absolute -bottom-12 right-6 md:-bottom-16 md:right-10">
                        <svg width="320" height="280" viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 279.5H319V0" stroke="url(#gradient2)" strokeWidth="1.5" />
                            <defs>
                                <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#D5A44F" />
                                    <stop offset="100%" stopColor="#166880" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
            </motion.div>

            {/* CONTACT INFO + FORM */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-24"
            >
                <div className="container mx-auto px-4">
                    <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
                        {/* Left: Contact Info */}
                        <div className="w-full lg:w-1/2 space-y-10">
                            <div>
                                <h2 className="text-[#166880] text-xl md:text-2xl font-medium tracking-wider font-inknut">
                                    {t('feelFree')}
                                </h2>
                                <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mt-3 font-konkhmer">
                                    {t('planDream')}
                                </h1>
                                <p className="text-white/80 text-sm md:text-base lg:text-lg leading-relaxed mt-5 font-inknut">
                                    {t('reachOutDesc')}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                                <div>
                                    <h3 className="text-[#D5A44F] text-lg md:text-xl font-bold tracking-wider font-konkhmer">
                                        {t('callCenter')}
                                    </h3>
                                    <p className="text-white text-base md:text-lg font-medium mt-3">{t('phone')}</p>
                                </div>

                                <div>
                                    <h3 className="text-[#D5A44F] text-lg md:text-xl font-bold tracking-wider font-konkhmer">
                                        {t('emailTitle')}
                                    </h3>
                                    <p className="text-white text-base md:text-lg font-medium mt-3">{t('email')}</p>
                                </div>

                                <div>
                                    <h3 className="text-[#D5A44F] text-lg md:text-xl font-bold tracking-wider font-konkhmer">
                                        {t('locationTitle')}
                                    </h3>
                                    <p className="text-white text-base md:text-lg font-medium leading-relaxed mt-3">
                                        {t('location')}
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-[#D5A44F] text-lg md:text-xl font-bold tracking-wider font-konkhmer">
                                        {t('socialTitle')}
                                    </h3>
                                    <div className="flex space-x-5 mt-4">
                                        <a href="https://web.facebook.com/profile.php?id=61584113124002" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                            <FaFacebookF className="h-8 w-8 text-[#166880] hover:text-[#D5A44F] transition-all duration-300 hover:scale-110" />
                                        </a>
                                        <a href="https://www.instagram.com/aamy.tours?igsh=Z21pOWk1dXJ4MzBn&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                            <FaInstagram className="h-8 w-8 text-[#166880] hover:text-[#D5A44F] transition-all duration-300 hover:scale-110" />
                                        </a>
                                        <a href="https://wa.me/+94770483364" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                            <FaWhatsapp className="h-8 w-8 text-[#166880] hover:text-[#D5A44F] transition-all duration-300 hover:scale-110" />
                                        </a>
                                        <a href="https://www.tiktok.com/@aamy_tours?lang=en" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                            <FaTiktok className="h-8 w-8 text-[#166880] hover:text-[#D5A44F] transition-all duration-300 hover:scale-110" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Contact Form */}
                        <div className="w-full lg:w-1/2">
                            <motion.div
                                initial={{ opacity: 0, x: 60 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                                className="bg-[#111]/90 backdrop-blur-md rounded-2xl p-8 md:p-12 shadow-2xl border border-white/10"
                            >
                                <h2 className="text-[#D5A44F] text-2xl md:text-3xl font-bold tracking-wider font-konkhmer">
                                    {t('getInTouch')}
                                </h2>
                                <p className="text-white/80 text-sm md:text-base leading-relaxed mt-3 font-inknut">
                                    {t('getInTouchDesc')}
                                </p>

                                <form className="mt-10 space-y-7" onSubmit={handleSubmit}>
                                    <div>
                                        <label className="block text-white text-sm md:text-base font-semibold tracking-wider">
                                            {t('nameLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#D5A44F] transition-all duration-300 placeholder-white/40"
                                            placeholder={t('namePlaceholder')}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm md:text-base font-semibold tracking-wider">
                                            {t('emailLabel')}
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#D5A44F] transition-all duration-300 placeholder-white/40"
                                            placeholder={t('emailPlaceholder')}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm md:text-base font-semibold tracking-wider">
                                            {t('phoneLabel')}
                                        </label>
                                        <input
                                            type="tel"
                                            name="contactNumber"
                                            value={formData.contactNumber}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#D5A44F] transition-all duration-300 placeholder-white/40"
                                            placeholder={t('phonePlaceholder')}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm md:text-base font-semibold tracking-wider">
                                            {t('messageLabel')}
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#D5A44F] transition-all duration-300 resize-none placeholder-white/40"
                                            rows={5}
                                            placeholder={t('messagePlaceholder')}
                                            required
                                        />
                                    </div>

                                    {successMessage && (
                                        <p className="text-[#D5A44F] text-sm md:text-base font-medium animate-pulse">
                                            {successMessage}
                                        </p>
                                    )}
                                    {errorMessage && (
                                        <p className="text-red-400 text-sm md:text-base font-medium">
                                            {errorMessage}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`mt-8 flex h-14 w-full items-center justify-center space-x-3 rounded-full px-10 font-bold text-white text-base md:text-lg transition-all duration-300 shadow-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'
                                            }`}
                                        style={{
                                            background: isSubmitting
                                                ? 'linear-gradient(to right, #0f4d5e, #b88a3a)'
                                                : 'linear-gradient(to right, #166880, #D5A44F)',
                                            boxShadow: '0 6px 20px rgba(22, 104, 128, 0.3)',
                                        }}
                                    >
                                        <span>{isSubmitting ? t('sendingButton') : t('sendButton')}</span>
                                        {!isSubmitting && (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-6 w-6">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />
                                            </svg>
                                        )}
                                    </button>
                                </form>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* MAP SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-10 lg:py-12"
            >
                <div className="container mx-auto px-4">
                    <h2
                        className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-16"
                        style={{
                            background: "linear-gradient(to right, #166880, #D5A44F)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            textShadow: '0 0 15px rgba(213, 164, 79, 0.3)',
                        }}
                    >
                        {t('findUsTitle')}
                    </h2>
                    <div className="flex justify-center">
                        <div className="w-full">
                            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5124.85133887226!2d80.2252239!3d6.045826499999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae173a8242c9623%3A0x74d3c79e60ebbb4d!2sAamy%20Tours%20Sri%20Lanka!5e1!3m2!1sen!2slk!4v1763553154744!5m2!1sen!2slk"
                                    width="100%"
                                    height="520"
                                    className="border-0"
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Aamy Tours Sri Lanka Location"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default ContactUs;